import { api } from '@/integrations/clients/carAuction/carAuctionClient';
import type {
  SupportGuideDto,
  CreateSupportGuideRequest,
  UpdateSupportGuideRequest,
} from '@/types/supportGuide';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  count?: number;
}

interface GetGuidesResponse {
  success: boolean;
  count?: number;
  data: SupportGuideDto[];
}

/** Public: get published support guides (e.g. type=video for video guides) */
export async function getSupportGuides(params?: {
  type?: string;
  category?: string;
}): Promise<SupportGuideDto[]> {
  const response = await api.get<GetGuidesResponse>('/support/guides', { params });
  if (!response.success || !response.data) return [];
  return Array.isArray(response.data) ? response.data : [];
}

/** Public: get the one guide shown on "Lägg upp annons > Bilder (Bild guide)" */
export async function getPhotoGuide(): Promise<SupportGuideDto | null> {
  const response = await api.get<ApiResponse<SupportGuideDto | null>>('/support/guides/photo-guide');
  if (!response.success) return null;
  return response.data ?? null;
}

/** Admin: get all video guides (no isPublished filter) */
export async function getSupportGuidesForAdmin(): Promise<SupportGuideDto[]> {
  const response = await api.get<GetGuidesResponse>('/admin/support/guides');
  if (!response.success || !response.data) return [];
  return Array.isArray(response.data) ? response.data : [];
}

/** Admin: create support guide (video). videoUrl = CDN_BASE + s3Key after upload. */
export async function createSupportGuide(
  request: CreateSupportGuideRequest
): Promise<SupportGuideDto> {
  const response = await api.post<ApiResponse<SupportGuideDto>>('/admin/support/guides', request);
  if (!response.success || !response.data) {
    throw new Error('Failed to create support guide');
  }
  return response.data;
}

/** Admin: update support guide */
export async function updateSupportGuide(
  id: string,
  request: UpdateSupportGuideRequest
): Promise<SupportGuideDto> {
  const response = await api.put<ApiResponse<SupportGuideDto>>(
    `/admin/support/guides/${id}`,
    request
  );
  if (!response.success || !response.data) {
    throw new Error('Failed to update support guide');
  }
  return response.data;
}

/** Admin: delete support guide (and video from S3 if CDN URL) */
export async function deleteSupportGuide(id: string): Promise<void> {
  const response = await api.delete<ApiResponse<unknown>>(
    `/admin/support/guides/${id}`
  );
  if (!response.success) {
    throw new Error('Failed to delete support guide');
  }
}

/** Admin: set this guide as the one shown on "Lägg upp annons > Bilder (Bild guide)". Max 1 at a time. */
export async function setPhotoGuide(id: string): Promise<SupportGuideDto> {
  const response = await api.patch<ApiResponse<SupportGuideDto>>(
    `/admin/support/guides/${id}/photo-guide`
  );
  if (!response.success || !response.data) {
    throw new Error('Failed to set photo guide');
  }
  return response.data;
}
